/**
 * Urgency Popup Web Component
 * Displays promotional urgency messages for conversion optimization
 */
(function() {
  'use strict';

  class UrgencyPopup extends HTMLElement {
    constructor() {
      super();
      this.config = {
        enabled: this.dataset.enabled === 'true',
        minPurchases: parseInt(this.dataset.minPurchases) || 15,
        maxPurchases: parseInt(this.dataset.maxPurchases) || 89,
        minHours: parseInt(this.dataset.minHours) || 2,
        maxHours: parseInt(this.dataset.maxHours) || 24,
        showScarcity: this.dataset.showScarcity === 'true',
        nextPopupDelay: (parseInt(this.dataset.nextPopupDelay) || 13) * 1000,
        initialDelay: parseInt(this.dataset.initialDelay) || 15000,
        autoHideDelay: (parseInt(this.dataset.autoHide) || 8) * 1000,
        productId: this.dataset.productId,
        debugMode: this.dataset.debug === 'true'
      };

      this.popups = [];
      this.currentPopupIndex = 0;
      this.sessionKey = `urgency_shown_${this.config.productId}`;
      
      this.popupTriggered = false;
      
      // Touch/swipe tracking
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isDragging = false;

      // Mobile auto-show tracking
      this.firstPopupClosed = false;
      this.secondPopupTimer = null;
      this.secondPopupScheduled = false;

      if (this.config.debugMode) {
        console.log('🎯 Urgency Popup: Constructor initialized', this.config);
      }
      
      if (this.config.enabled) {
        this.init();
      } else if (this.config.debugMode) {
        console.warn('⚠️ Urgency Popup: Disabled in settings');
      }
    }

    init() {
      if (this.config.debugMode) {
        console.log('🎯 Urgency Popup: Init started');
      }
      
      // Debug mode: Skip session check and show immediately
      if (!this.config.debugMode) {
        // Check session state with cooldown
        const sessionData = this.getSessionState();
        
        if (sessionData.hasSeenPopup) {
          const timeSinceLastSeen = Date.now() - sessionData.lastShown;
          const cooldownPeriod = 10 * 60 * 1000; // 10 minutes
          
          if (timeSinceLastSeen < cooldownPeriod) {
            if (this.config.debugMode) {
              console.log('⏸️ Urgency Popup: Cooldown active, not showing');
            }
            return; // Don't show again too soon
          }
        }
      }

      this.popups = Array.from(this.querySelectorAll('.urgency-popup'));
      
      if (this.config.debugMode) {
        console.log(`🎯 Found ${this.popups.length} popup elements`);
      }
      
      if (this.popups.length === 0) {
        if (this.config.debugMode) {
          console.warn('⚠️ No popup elements found in DOM');
        }
        return;
      }

      // Generate metrics with session persistence
      this.generateFakeMetrics();

      // Set up close buttons
      this.setupCloseButtons();

      // Set up swipe gestures
      this.setupSwipeGestures();

      // Debug mode: Show immediately without delay, don't auto-hide
      if (this.config.debugMode) {
        this.popups.forEach(popup => {
          popup.classList.add('is-visible');
        });
        console.log('🔥 Urgency Popup Debug Mode: Popups visible for styling');
      } else {
        // Normal mode: Simple time-based triggering
        setTimeout(() => {
          this.showNextPopup();
          this.markAsShown();
        }, this.config.initialDelay);
      }
    }

    generateFakeMetrics() {
      // Get consistent session metrics instead of random ones
      const metrics = this.getOrCreateSessionMetrics();

      // Update purchase popup with custom text
      const purchasePopup = this.querySelector('[data-popup-type="purchase"]');
      if (purchasePopup) {
        const messageEl = purchasePopup.querySelector('.urgency-popup__message');
        const template = messageEl.getAttribute('data-template');
        
        if (template && template.trim() !== '') {
          // Use custom template
          const hourText = metrics.hours === 1 ? '1 hour' : `${metrics.hours} hours`;
          let html = template
            .replace('{count}', `<span class="urgency-popup__number">${metrics.purchases}</span>`)
            .replace('{hours}', `<span class="urgency-popup__time">${hourText}</span>`)
            .replace('People', `<span class="urgency-popup__highlight">People</span>`);
          messageEl.innerHTML = html;
        } else {
          // Fallback to default
          const purchaseNumber = purchasePopup.querySelector('[data-metric="purchases"]');
          const hoursElement = purchasePopup.querySelector('[data-metric="hours"]');
          if (purchaseNumber) purchaseNumber.textContent = metrics.purchases;
          if (hoursElement) hoursElement.textContent = `${metrics.hours} hours`;
        }
      }

      // Update scarcity popup with custom text
      const scarcityPopup = this.querySelector('[data-popup-type="scarcity"]');
      if (scarcityPopup && this.config.showScarcity) {
        const messageEl = scarcityPopup.querySelector('.urgency-popup__message');
        const template = messageEl.getAttribute('data-template');
        
        if (template && template.trim() !== '') {
          // Use custom template
          let html = template
            .replace('{time}', `<span class="urgency-popup__countdown">${metrics.timeLeft}</span>`)
            .replace('sold out', `<span class="urgency-popup__highlight">sold out</span>`);
          messageEl.innerHTML = html;
        } else {
          // Fallback to default
          const countdownElement = scarcityPopup.querySelector('[data-metric="countdown"]');
          if (countdownElement) {
            countdownElement.textContent = metrics.timeLeft;
          }
        }
      }
    }

    getOrCreateSessionMetrics() {
      const storageKey = `urgency_metrics_${this.config.productId}`;
      const existing = sessionStorage.getItem(storageKey);
      
      if (existing) {
        const data = JSON.parse(existing);
        
        // Check if data is "stale" (older than 15 minutes)
        const age = Date.now() - data.created;
        const fifteenMinutes = 15 * 60 * 1000;
        
        if (age > fifteenMinutes) {
          // Create evolved metrics (slight changes to feel "live")
          return this.evolveMetrics(data, storageKey);
        }
        
        // Data is fresh, return as-is
        return data;
      }
      
      // No existing data, create fresh
      return this.createFreshMetrics(storageKey);
    }

    createFreshMetrics(storageKey) {
      const timeLeftNumber = this.randomInt(25, 55);
      const metrics = {
        purchases: this.randomInt(this.config.minPurchases, this.config.maxPurchases),
        hours: this.randomInt(this.config.minHours, this.config.maxHours),
        timeLeft: timeLeftNumber + ' minutes',
        timeLeftNumber: timeLeftNumber,
        created: Date.now(),
        lastUpdated: Date.now()
      };
      
      sessionStorage.setItem(storageKey, JSON.stringify(metrics));
      return metrics;
    }

    evolveMetrics(oldData, storageKey) {
      // Create realistic evolution of numbers
      const timePassed = Math.floor((Date.now() - oldData.lastUpdated) / (1000 * 60)); // minutes
      
      const newTimeLeft = Math.max(10, oldData.timeLeftNumber - this.randomInt(2, 8));
      const evolved = {
        purchases: Math.min(
          this.config.maxPurchases,
          oldData.purchases + this.randomInt(0, Math.max(1, timePassed))
        ),
        hours: oldData.hours, // Keep hours same
        timeLeft: newTimeLeft + ' minutes',
        timeLeftNumber: newTimeLeft,
        created: oldData.created,
        lastUpdated: Date.now()
      };
      
      sessionStorage.setItem(storageKey, JSON.stringify(evolved));
      return evolved;
    }

    getSessionState() {
      const key = `urgency_state_${this.config.productId}`;
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : { hasSeenPopup: false, lastShown: 0 };
    }

    markAsShown() {
      const key = `urgency_state_${this.config.productId}`;
      const state = {
        hasSeenPopup: true,
        lastShown: Date.now()
      };
      sessionStorage.setItem(key, JSON.stringify(state));
    }


    randomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    isMobile() {
      return window.innerWidth < 768;
    }

    setupCloseButtons() {
      this.popups.forEach(popup => {
        const closeBtn = popup.querySelector('.urgency-popup__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.hidePopup(popup);
          });
        }
      });
    }

    setupSwipeGestures() {
      this.popups.forEach(popup => {
        // Touch events for mobile
        popup.addEventListener('touchstart', (e) => {
          this.handleTouchStart(e, popup);
        }, { passive: true });

        popup.addEventListener('touchmove', (e) => {
          this.handleTouchMove(e, popup);
        }, { passive: false });

        popup.addEventListener('touchend', (e) => {
          this.handleTouchEnd(e, popup);
        }, { passive: true });

        // Mouse events for desktop
        popup.addEventListener('mousedown', (e) => {
          this.handleMouseStart(e, popup);
        });

        popup.addEventListener('mousemove', (e) => {
          this.handleMouseMove(e, popup);
        });

        popup.addEventListener('mouseup', (e) => {
          this.handleMouseEnd(e, popup);
        });

        popup.addEventListener('mouseleave', (e) => {
          this.handleMouseEnd(e, popup);
        });
      });
    }

    handleTouchStart(e, popup) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.isDragging = false;
      popup.style.transition = 'none';
    }

    handleTouchMove(e, popup) {
      if (!this.touchStartX) return;

      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - this.touchStartX;
      const deltaY = touchY - this.touchStartY;

      // Only track horizontal swipes (ignore vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this.isDragging = true;
        e.preventDefault(); // Prevent page scroll during swipe
        
        // Apply visual feedback for left swipe
        if (deltaX < 0) {
          const opacity = Math.max(0, 1 + (deltaX / 150));
          const translateX = Math.max(-150, deltaX);
          popup.style.transform = `translateX(${translateX}px)`;
          popup.style.opacity = opacity;
        }
      }
    }

    handleTouchEnd(e, popup) {
      if (!this.touchStartX || !this.isDragging) {
        popup.style.transition = '';
        popup.style.transform = '';
        popup.style.opacity = '';
        this.touchStartX = 0;
        this.isDragging = false;
        return;
      }

      const touchX = e.changedTouches[0].clientX;
      const deltaX = touchX - this.touchStartX;
      const swipeThreshold = 80; // Minimum distance to trigger close

      // Left swipe to close
      if (deltaX < -swipeThreshold) {
        this.hidePopup(popup);
        if (this.config.debugMode) {
          console.log('🎯 Popup closed via left swipe');
        }
      } else {
        // Snap back if swipe wasn't far enough
        popup.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        popup.style.transform = '';
        popup.style.opacity = '';
      }

      this.touchStartX = 0;
      this.isDragging = false;
    }

    handleMouseStart(e, popup) {
      this.touchStartX = e.clientX;
      this.touchStartY = e.clientY;
      this.isDragging = false;
      popup.style.transition = 'none';
      popup.style.cursor = 'grabbing';
    }

    handleMouseMove(e, popup) {
      if (!this.touchStartX) return;

      const deltaX = e.clientX - this.touchStartX;
      const deltaY = e.clientY - this.touchStartY;

      // Only track horizontal drags
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this.isDragging = true;
        
        // Apply visual feedback for left drag
        if (deltaX < 0) {
          const opacity = Math.max(0, 1 + (deltaX / 150));
          const translateX = Math.max(-150, deltaX);
          popup.style.transform = `translateX(${translateX}px)`;
          popup.style.opacity = opacity;
        }
      }
    }

    handleMouseEnd(e, popup) {
      if (!this.touchStartX || !this.isDragging) {
        popup.style.transition = '';
        popup.style.transform = '';
        popup.style.opacity = '';
        popup.style.cursor = '';
        this.touchStartX = 0;
        this.isDragging = false;
        return;
      }

      const deltaX = e.clientX - this.touchStartX;
      const swipeThreshold = 80; // Minimum distance to trigger close

      // Left drag to close
      if (deltaX < -swipeThreshold) {
        this.hidePopup(popup);
        if (this.config.debugMode) {
          console.log('🎯 Popup closed via left drag');
        }
      } else {
        // Snap back if drag wasn't far enough
        popup.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        popup.style.transform = '';
        popup.style.opacity = '';
      }

      popup.style.cursor = '';
      this.touchStartX = 0;
      this.isDragging = false;
    }

    showNextPopup() {
      if (this.currentPopupIndex >= this.popups.length) return;

      const popup = this.popups[this.currentPopupIndex];
      this.showPopup(popup);
      this.currentPopupIndex++;

      // On mobile, only show first popup initially - second popup will be triggered after first is closed
      if (this.isMobile()) {
        if (this.config.debugMode) {
          console.log('🎯 Mobile: Showing only first popup, second will auto-show after close');
        }
        return; // Don't auto-schedule second popup on mobile
      }

      // Desktop behavior: Schedule next popup if scarcity is enabled - 1s delay, both stay visible
      if (this.config.showScarcity && this.currentPopupIndex < this.popups.length) {
        setTimeout(() => {
          this.showNextPopup();
        }, this.config.nextPopupDelay); 
      }
    }

    showPopup(popup) {
      // Animate in
      requestAnimationFrame(() => {
        popup.classList.add('is-visible');
      });

      // Start progress bar
      const progressBar = popup.querySelector('.urgency-popup__progress-bar');
      if (progressBar) {
        progressBar.style.transitionDuration = `${this.config.autoHideDelay}ms`;
        requestAnimationFrame(() => {
          progressBar.style.width = '100%';
        });
      }

      // Auto hide
      setTimeout(() => {
        this.hidePopup(popup);
      }, this.config.autoHideDelay);
    }

    hidePopup(popup) {
      popup.classList.add('is-hiding');
      popup.classList.remove('is-visible');

      // Reset styles that might have been applied during swipe
      popup.style.transition = '';
      popup.style.transform = '';
      popup.style.opacity = '';
      popup.style.cursor = '';

      // Check if this is the first popup being closed on mobile - but only schedule once
      const isFirstPopup = popup.dataset.popupType === 'purchase';
      if (isFirstPopup && this.isMobile() && this.config.showScarcity && !this.secondPopupScheduled) {
        this.firstPopupClosed = true;
        this.secondPopupScheduled = true; // Prevent multiple scheduling
        
        // Clear any existing timer (safety)
        if (this.secondPopupTimer) {
          clearTimeout(this.secondPopupTimer);
        }
        
        // Show second popup after 5 seconds
        this.secondPopupTimer = setTimeout(() => {
          const secondPopup = this.querySelector('[data-popup-type="scarcity"]');
          if (secondPopup && !secondPopup.classList.contains('is-visible')) {
            this.showPopup(secondPopup);
            if (this.config.debugMode) {
              console.log('🎯 Mobile: Auto-showing second popup after first was closed');
            }
          }
        }, 5000);
        
        if (this.config.debugMode) {
          console.log('🎯 Mobile: First popup closed, second popup scheduled in 5s (one-time)');
        }
      }

      // Reset for potential reuse
      setTimeout(() => {
        popup.classList.remove('is-hiding');
        const progressBar = popup.querySelector('.urgency-popup__progress-bar');
        if (progressBar) {
          progressBar.style.width = '0';
          progressBar.style.transitionDuration = '0s';
        }
      }, 400);
    }
  }

  // Register custom element
  if ('customElements' in window && !customElements.get('urgency-popup')) {
    customElements.define('urgency-popup', UrgencyPopup);
  }
})();

